import { ErrorPage } from "@backstage/core-components";
import {
  configApiRef,
  discoveryApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { isAppProxyActive, requestAppProxyProbe } from "./sessionGuard";

/**
 * Mecanismo de recuperación para el problema de identidad stale de
 * ProxiedSignInPage: tras un probe de App Proxy "valid" (cookie de sesión
 * viva) el permiso de un RequirePermission puede seguir fallando porque el
 * AppRouter conserva el token backstageIdentity caducado emitido al montar.
 * Un reload lo arregla porque recrea el AppRouter; aquí lo sustituimos por
 * un remount controlado (keyed Fragment) que recrea el AppRouter — y con él
 * la identidad — sin location.reload.
 *
 * Reglas:
 * - Máximo UN remount por carga de la app (guard canRemount). Tras el
 *   remount, si el gate vuelve a fallar, PermissionGateErrorPage renderiza
 *   el 404 estándar definitivo: RBAC deny real nunca concede contenido.
 * - Solo un probe "valid" remonta. "expired" ya notifica la expiración
 *   global vía SessionExpiredDialog; "unavailable" es ambiguo. Ninguno
 *   remonta.
 * - Rutas inexistentes (NotFoundErrorPage -> AppProxyAwareNotFoundPage)
 *   NO participan en este mecanismo.
 */

interface AppProxyRemountContextValue {
  canRemount: boolean;
  triggerRemount: () => void;
}

const noRemount: AppProxyRemountContextValue = {
  canRemount: false,
  triggerRemount: () => {},
};

const AppProxyRemountContext =
  createContext<AppProxyRemountContextValue>(noRemount);

export const AppProxyRemountGate = ({ children }: { children: ReactNode }) => {
  // Estado único: key de remount + si aún queda el intento. La actualización
  // funcional deduplica gates concurrentes (varios RequirePermission
  // fallando a la vez solo consumen el intento una vez).
  const [{ remountKey, canRemount }, setState] = useState({
    remountKey: 0,
    canRemount: true,
  });

  const triggerRemount = useCallback(() => {
    setState((s) =>
      s.canRemount ? { remountKey: s.remountKey + 1, canRemount: false } : s,
    );
  }, []);

  return (
    <AppProxyRemountContext.Provider value={{ canRemount, triggerRemount }}>
      {/* Fragment con key: fuerza remount completo del subárbol (incluido
          AppRouter y su identidad ProxiedSignInPage) al cambiar remountKey. */}
      <Fragment key={remountKey}>{children}</Fragment>
    </AppProxyRemountContext.Provider>
  );
};

/**
 * errorPage para los RequirePermission de rutas protegidas.
 *
 * Renderiza SIEMPRE el 404 estándar (nunca concede permiso localmente).
 * En paralelo, si App Proxy está activo y aún queda el intento de remount,
 * lanza el probe compartido: solo un resultado "valid" (cookie viva =>
 * identidad stale, no expiración) consume el remount del AppRouter.
 */
export const PermissionGateErrorPage = () => {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { canRemount, triggerRemount } = useContext(AppProxyRemountContext);

  const appProxyActive = isAppProxyActive(
    configApi,
    typeof window !== "undefined" ? window.location.origin : "",
  );

  useEffect(() => {
    if (!appProxyActive || !canRemount) {
      return undefined;
    }
    let cancelled = false;
    void requestAppProxyProbe(discoveryApi).then((result) => {
      // ponytail: solo "valid" remonta; expired/unavailable dejan el 404.
      if (!cancelled && result.status === "valid") {
        triggerRemount();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [appProxyActive, canRemount, discoveryApi, triggerRemount]);

  return <ErrorPage status="404" statusMessage="PAGE NOT FOUND" />;
};
